<template>
  <div id="app">
    <header-modals :is-mobile="isMobile" />
    <page-error v-if="isCookiesAccepted === false" :is-mobile="isMobile">
      <template #title>
        {{ $t('CP.cookiesPageTitle') }}
      </template>
      <template #message>
        {{ $t('CP.cookiesPageMessage') }}
      </template>
      <button-app @clicked="allowCookiesReload" class="server-error-button">
        {{ $t('CP.cookiesPageBttn') }}
      </button-app>
    </page-error>
    <page-error v-else-if="initialLoadStatus === 'error' && this.$route.name !== 'Page 404'"
                :is-mobile="isMobile" :showHeader="!!user.uid">
      <template #title>
        {{ $t('CP.serveErrorPageTitle') }}
      </template>
      <template #message>
        {{ $t('CP.serveErrorPageMessage') }}
      </template>
      <button-app @clicked="reload" class="server-error-button">
        {{ $t('CP.serveErrorPageBttn') }}
      </button-app>
    </page-error>
    <check-user-page v-else-if="isUserFormVisible" />
    <router-view v-else-if="initialLoadStatus !== 'loading'" :is-mobile="isMobile"/>
    <transition :name="cookiesTransitionName">
      <div  class="cookies-popup"
            v-if="!(isCookiesAccepted === true || isCookiesAccepted === false)">
        <p>{{ $t('CP.allowCookiesMessage') }}</p>
        <button @click="acceptCookies(false)" class="cookies-popup-button cookies-popup-decline">
          {{ $t('CP.declineCookiesBttn') }}
        </button>
        <button @click="acceptCookies(true)" class="cookies-popup-button cookies-popup-accept">
          {{ $t('CP.allowCookiesBttn') }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { LOAD_INITIAL_DATA_REQUEST, SET_LANGUAGE } from './store/app/types';
import HeaderModals from './modals/HeaderModals/HeaderModals';
import CheckUserPage from './pages/CheckUserPage';
import PageError from './layouts/PageError/PageError';
import ButtonApp from './components/Button/ButtonApp';
import './plugins/shepherd';

export default {
  name: 'App',
  components: {
    HeaderModals,
    CheckUserPage,
    PageError,
    ButtonApp,
  },
  data() {
    return {
      isMobile: false,
      isCookiesAccepted: false,
      cookiesTransitionName: '',
    };
  },
  created() {
    this.detectMobile();
  },
  mounted() {
    this.cookiesTransitionName = 'fade';

    this.checkCookies();
    this.initialiseLocale();
    window.addEventListener('resize', this.detectMobile, { passive: true });
    this.loadInitialData()
      .then(() => {})
      .catch(() => {});
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.detectMobile);
    }
  },
  methods: {
    checkCookies() {
      const isCookiesAccepted = localStorage.getItem('isCookiesAccepted');
      if (isCookiesAccepted === 'true') {
        this.isCookiesAccepted = true;
      } else if (isCookiesAccepted === 'false') {
        this.isCookiesAccepted = false;
      } else {
        this.isCookiesAccepted = null;
      }
    },
    acceptCookies(answer) {
      localStorage.setItem('isCookiesAccepted', answer);
      this.isCookiesAccepted = answer;
    },
    allowCookiesReload() {
      localStorage.setItem('isCookiesAccepted', true);
      window.location.reload();
    },
    detectMobile() {
      // console.log(document.documentElement.clientHeight, window.innerHeight);
      document.body.style.height = `${window.innerHeight - 1}px`;
      this.isMobile = window.innerWidth <= 768 || document.documentElement.clientWidth <= 768;
    },
    async loadInitialData() {
      if (this.$store.getters.isAuthenticated) {
        await this.$store.dispatch(LOAD_INITIAL_DATA_REQUEST);
      }
    },
    initialiseLocale() {
      const storageLocale = localStorage.getItem('locale');

      if (storageLocale || this.$store.state.app.currentLanguage) {
        this.$i18n.locale = storageLocale || this.$store.state.app.currentLanguage;
      }

      this.$store.dispatch(SET_LANGUAGE, this.$i18n.locale);
      this.$moment.locale(this.$i18n.locale);
    },
    checkIfUserHasNoInitials() {
      const { user } = this.$store.state.user;
      if (!(this.$route.path.indexOf('/events') || this.$route.path.indexOf('/people'))) return;
      if (user && user.user && !user.full_name) {
        this.$router.push('/');
      }
    },
    reload() {
      window.location.reload();
    },
  },
  title() {
    let title = 'Repay';
    const subTitle = this.$route.path.split('/')[1];
    if (subTitle) {
      title += ` - ${subTitle[0].toUpperCase() + subTitle.slice(1)}`;
    }
    return title;
  },
  computed: {
    initialLoadStatus() {
      return this.$store.state.app.initialStatus;
    },
    isUserInfoFilled() {
      return this.$store.getters.isUserInfoFilled;
    },
    isUserFormVisible() {
      return this.initialLoadStatus === 'success'
              && !this.isUserInfoFilled
              && (this.$route.name !== 'SignUpPage')
              && (this.$route.name !== 'SignInPage');
    },
    user() {
      return this.$store.getters.getProfile;
    },
  },
};
</script>

<style lang="scss">
@import "assets/styles/bootstrap/bootstrap";
@import "assets/styles/shepherd/shepherd";
@import "assets/styles/common/Global";

html, body {
  min-width: 1025px;
  min-height: 355px;
}

#app {
  box-sizing: border-box;
  font-family: 'SF UI Display', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  font-size: 14px;
  min-height: 100%;
  height: 100%;
  min-width: 100%;
  background-color: var(--backgound-color);
  color: var(--text-main-color);
}

.cookies-popup {
  align-items: center;
  background-color: var(--text-main-color);
  bottom: 0;
  box-sizing: border-box;
  color: #fff;
  display: flex;
  height: 60px;
  padding: 12px 32px;
  position: fixed;
  text-align: left;
  width: 100%;
  z-index: 50;

  p {
    margin-bottom: 0;
    margin-right: auto;

    @media screen and (max-width: $breakpoint-sm) {
    }
  }
}
.cookies-popup-button {
  appearance: none;
  background: none;
  border: none;
  border-radius: 4px;
  color: #fff;
  flex-shrink: 0;
  font-family: inherit;
  height: 38px;
  outline: none;
  padding: 0;

  &:focus {
    outline: none;
  }
}
.cookies-popup-accept {
  background-color: #3E4670;
  width: 150px;
}
.cookies-popup-decline {
  color: #A9B1E2;
  margin-right: 10px;
  width: 100px;
}

.fade-leave-active {
  transition: .225s ease;
}
.fade-leave-to {
  opacity: 0;
  transform: translate(0, 100%);
}

@media screen and (min-width: $breakpoint-sm) and (max-width: $breakpoint-lg) {
  html, body {
    max-width: $breakpoint-lg;
    min-width: $breakpoint-sm;
    min-height: 100%;
  }
}

@media screen and (max-width: $breakpoint-sm) {
  html, body {
    max-width: $breakpoint-sm;
    min-width: 375px;
    min-height: 100%;
  }

  .cookies-popup {
    flex-wrap: wrap;
    justify-content: center;
    padding: 24px 32px;
    height: auto;

    p {
      margin-bottom: 12px;
      text-align: center;
      width: 100%;
    }
  }
}
</style>
